import { useEffect, useRef } from "react";
import { useAuthStore } from "../store";
import { useFYStore } from "@/modules/financialYear";
import { api } from "@/api/client";
import { meQueryKey, type MeResponse } from "@/api/hooks/auth";
import { useQueryClient } from "@tanstack/react-query";

/**
 * On mount, rotate the refresh-token (httpOnly cookie) → call /me. Hydrates
 * auth + FY stores. Resolves whether or not the user is authenticated
 * (failure = anonymous).
 *
 * The access token is persisted in localStorage so that page reloads see a
 * valid Authorization header immediately, before this hook completes. The
 * session-expiry pill anchors to `sessionExpiresAt` returned by the server,
 * which is rooted at the refresh-token family's `family_started_at` and does
 * NOT slide on silent rotation.
 */
export function useBootstrapAuth() {
  const qc = useQueryClient();
  const setAuth = useAuthStore((s) => s.setAuth);
  const setHydrated = useAuthStore((s) => s.setHydrated);
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setServerCurrent = useFYStore((s) => s.setServerCurrent);
  const setSelected = useFYStore((s) => s.setSelected);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    let aborted = false;
    const watchdog = setTimeout(() => {
      if (!aborted) {
        console.warn("[bootstrap] timeout after 8s, forcing anonymous");
        setHydrated(true);
      }
    }, 8000);
    (async () => {
      try {
        const refresh = await api.post<{
          accessToken: string;
          sessionExpiresAt?: string | null;
        }>("/auth/refresh", null, {
          headers: { "X-Auto-Refresh": "1" },
        });
        if (aborted) return;
        const accessToken = refresh.data?.accessToken;
        if (!accessToken) throw new Error("No accessToken in refresh response");
        const sessionExpiresAt = refresh.data?.sessionExpiresAt ?? null;
        setAccessToken(accessToken, sessionExpiresAt);

        const me = await api.get<MeResponse>("/me", {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (aborted) return;
        qc.setQueryData(meQueryKey, me.data);
        setAuth({
          user: me.data.user,
          accessToken,
          permissions: me.data.permissions,
          authVersion: me.data.authVersion,
          sessionExpiresAt: me.data.sessionExpiresAt ?? sessionExpiresAt,
        });
        setServerCurrent(me.data.currentFinancialYear);
        if (!me.data.selectedFinancialYear) setSelected(me.data.currentFinancialYear);
      } catch (err) {
        console.warn("[bootstrap] anonymous", err);
        if (aborted) return;
        // Persisted user but refresh rejected → real session termination.
        // Anonymous-from-boot stays anonymous.
        const hadPersistedUser = !!useAuthStore.getState().user;
        if (hadPersistedUser) {
          useAuthStore.getState().forceLogout();
        } else {
          setHydrated(true);
        }
      } finally {
        clearTimeout(watchdog);
      }
    })();

    return () => {
      aborted = true;
      clearTimeout(watchdog);
    };
  }, [qc, setAuth, setHydrated, setAccessToken, setServerCurrent, setSelected]);
}
